import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

export const projectedEarningsApi = createApi({
  reducerPath: "projectedApi",
  tagTypes: ["ProjectedApi"],
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getProjectedEarnings: builder.query({
      query: ({ name, year }) => ({
        url: `/stats/portal/get-projected-earnings-stats`,
        method: "POST",
        body: {
          name,
          year,
        },
      }),
      providesTags: ["ProjectedApi"],
      transformResponse: (response) => response.data,
    }),
    getProjectedPayments: builder.query({
      query: ({ name, year }) => ({
        url: `/stats/portal/get-projected-payments`,
        method: "POST",
        body: {
          name,
          year,
        },
      }),
      providesTags: ["ProjectedApi"],
      transformResponse: (response) => response.data,
    }),
    getStillOwed: builder.query({
      query: ({ name, year }) => ({
        url: `/stats/portal/get-still-owed`,
        method: "POST",
        body: {
          name,
          year,
        },
      }),
      providesTags: ["ProjectedApi"],
      transformResponse: (response) => response.data,
    }),
    getPaidToDate: builder.query({
      query: ({ name, year }) => ({
        url: `/stats/portal/get-paid-to-date`,
        method: "POST",
        body: {
          name,
          year,
        },
      }),
      providesTags: ["ProjectedApi"],
      transformResponse: (response) => response.data,
    }),
    getEstimatedDeduction: builder.query({
      query: ({ name, year }) => ({
        url: `/stats/portal/get-estimated-deduction`,
        method: "POST",
        body: {
          name,
          year,
        },
      }),
      providesTags: ["ProjectedApi"],
      transformResponse: (response) => response.data,
    }),
    getDownlineRevenueDetail: builder.query({
      query: ({ repName, year }) => ({
        url: `/sales/portal/get-downline-revenue-detail`,
        method: "POST",
        body: {
          repName,
          year
        },
      }),
      providesTags: ["ProjectedApi"],
    }),
    getPayrollOverride: builder.query({
      query: ({ name, year }) => ({
        url: `/stats/portal/get-payroll-override`,
        method: "POST",
        body: {
          name,
          year,
        },
      }),
      providesTags: ["ProjectedApi"],
      transformResponse: (response) => response.data,
    }),
    getRentBreakup: builder.query({
      query: ({ year }) => ({
        url: `/stats/portal/get-rent-breakup`,
        method: "POST",
        body: {
          year,
        },
      }),
      providesTags: ["ProjectedApi"],
      transformResponse: (response) => response.data,
    }),
    getDeductionBreakup: builder.query({
      query: ({ name, year }) => ({
        url: `/stats/portal/get-deduction-breakup`,
        method: "POST",
        body: {
          name,
          year,
        },
      }),
      providesTags: ["ProjectedApi"],
      transformResponse: (response) => response.data,
    }),
    getBonus: builder.query({
      query: ({ name, year }) => ({
        url: `/stats/portal/get-bonus-amount`,
        method: "POST",
        body: {
          name,
          year,
        },
      }),
      providesTags: ["ProjectedApi"],
      transformResponse: (response) => response.data,
    }),
    // Share this Payroll page (Projected Earnings / Vision Board) to a Friend:
    // create-or-dedup the Friend and text them a 1-day read-only link in one
    // call (the engine merged in Piece 1). Levi 2026-06-11, thread 249.
    sharePage: builder.mutation({
      query: ({ pageType, firstName, lastName, phone, email, notes }) => ({
        url: `/user/portal/share-page`,
        method: "POST",
        body: { pageType, firstName, lastName, phone, email, notes },
      }),
    }),
    // Share the Fantasy Sales League to a Friend (Phase A FSL Share button):
    // create-or-dedup the Friend and text them the rep's Arena invite link
    // (no TTL; lets them sign in and draft). Levi 2026-06-11, thread 254.
    shareArena: builder.mutation({
      query: ({ firstName, lastName, phone, email, notes, arenaMode }) => ({
        url: `/user/portal/arena-invite/share`,
        method: "POST",
        // Phase B: arenaMode "view" shares a read-only link; default/omitted = play.
        body: { firstName, lastName, phone, email, notes, arenaMode },
      }),
    }),
  }),
});

export const {
  useGetProjectedEarningsQuery,
  useGetProjectedPaymentsQuery,
  useGetStillOwedQuery,
  useGetPaidToDateQuery,
  useGetEstimatedDeductionQuery,
  useGetDownlineRevenueDetailQuery,
  useGetPayrollOverrideQuery,
  useGetRentBreakupQuery,
  useGetDeductionBreakupQuery,
  useGetBonusQuery,
  useSharePageMutation,
  useShareArenaMutation,
} = projectedEarningsApi;
